
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Clock, Play, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";

interface AutomationLog {
  id: string;
  task_name: string;
  status: 'success' | 'failed' | 'running';
  message: string;
  metadata: any;
  created_at: string;
}

export const AutomationManager = () => {
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const { toast } = useToast();

  const fetchLogs = async () => {
    try {
      console.log('🔍 Fetching automation logs...');
      
      // Use the automated-usda-sync function to fetch logs
      const { data, error } = await supabase.functions.invoke('automated-usda-sync', {
        body: { 
          fetchLogs: true 
        }
      });

      if (error) {
        console.error('Error fetching automation logs:', error);
        setLogs([]);
        toast({
          title: "Info",
          description: "Automation logs not available yet. Run a sync to create the first log.",
          variant: "default"
        });
        return;
      }

      if (data?.logs && Array.isArray(data.logs)) {
        console.log(`📊 Received ${data.logs.length} automation logs`);
        setLogs(data.logs);
      } else {
        console.log('No logs returned from function');
        setLogs([]);
      }
    } catch (error) {
      console.error('Error fetching automation logs:', error);
      setLogs([]);
      toast({
        title: "Info",
        description: "Automation logs not available yet. Run a sync to create the first log.",
        variant: "default"
      });
    } finally {
      setLoading(false);
    }
  };

  const triggerManualSync = async () => {
    setTriggering(true);
    try {
      console.log('🚀 Triggering manual USDA sync...');
      
      const { data, error } = await supabase.functions.invoke('automated-usda-sync', {
        body: { 
          automated: true, 
          trigger: 'manual' 
        }
      });

      if (error) throw error;

      console.log('Manual sync response:', data);

      toast({
        title: "Sync Triggered",
        description: data?.message || "USDA reports sync has been triggered manually",
        variant: "default"
      });

      // Refresh logs after a short delay
      setTimeout(fetchLogs, 2000);
      
    } catch (error: any) {
      console.error('Error triggering manual sync:', error);
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to trigger USDA reports sync",
        variant: "destructive"
      });
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">✅ Success</Badge>;
      case 'failed':
        return <Badge variant="destructive">❌ Failed</Badge>;
      case 'running':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">🔄 Running</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-green-600" />
            <span>USDA Reports Automation</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={fetchLogs}
              variant="outline"
              size="sm"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={triggerManualSync}
              disabled={triggering}
              size="sm"
            >
              <Play className="h-4 w-4 mr-1" />
              {triggering ? 'Triggering...' : 'Manual Sync'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Schedule Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 mb-1">Automated Schedule</h3>
              <p className="text-sm text-blue-700">
                <strong>Every Friday at 6:00 PM UTC</strong><br/>
                Automatically fetches USDA reports and matches them to all user profiles
              </p>
            </div>
          </div>
        </div>

        {/* Recent Execution Logs */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-3">Recent Executions</h3>
          
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-gray-200 h-16 rounded-lg"></div>
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No automation logs found</p>
              <p className="text-sm">Try running a manual sync to see logs</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(log.status)}
                      <span className="text-sm text-gray-500">
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                    {log.status === 'success' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : log.status === 'failed' ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-2">{log.message}</p>
                  
                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                      {log.metadata.reports_fetched && (
                        <span className="mr-4">
                          📥 Fetched: {log.metadata.reports_fetched}
                        </span>
                      )}
                      {log.metadata.reports_stored && (
                        <span className="mr-4">
                          💾 Stored: {log.metadata.reports_stored}
                        </span>
                      )}
                      {log.metadata.profiles_matched && (
                        <span>✅ Profiles Matched</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="text-sm text-green-700">
              <p className="font-medium mb-1">Automation Features:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Runs automatically every Friday at 6:00 PM UTC</li>
                <li>Fetches all USDA reports with keyword extraction</li>
                <li>Matches reports to all user profiles using enhanced radius-based logic</li>
                <li>Logs all execution results for monitoring</li>
                <li>Can be triggered manually for testing or immediate sync</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
